import { defineStore } from 'pinia';
import { FloatingButton } from '@/constants/types/FloatingButtons';
import useApi from "@/composeables/useApi";
import {DeliveredNotifications, PushNotifications, PushNotificationSchema} from "@capacitor/push-notifications";
import {isPlatform, SegmentChangeEventDetail} from "@ionic/vue";
import {Capacitor} from "@capacitor/core";

export enum NotificationType {
  Security = 'security',
  Regular = 'regular',
  Police = 'police',
  GroupPostPublished = 'group-post:published',
  GroupPostPending = 'group-post:pending',
  GroupPostComment = 'group-post:comment',
  GroupPostLike = 'group-post:like',
  GroupPostCommentLike = 'group-post:comment-like',
  GroupMembershipInvite = 'group-membership:invite',
  GroupMembershipRequest = 'group-membership:request',
  GroupMembershipMember = 'group-membership:member',
  GroupChecklistNew = 'group-checklist:new',
  Checklist = 'checklist',
  EmergencyReplyUpdated = 'emergency-reply:updated',
}

type SegmentOption = { label: string; value: string };

export const useUiStore = defineStore('uiStore', {
  state: () => ({
    segmentValue: '',
    segmentOptions: [] as SegmentOption[],
    incidentModalOpen: false,
    headFloatingButtons: [] as FloatingButton[],
    tabBadgeNumbers: {
      checklists: 0,
      groups: 0,
      warnings: 0,
    },
    notifications: [] as PushNotificationSchema[],
    dynamicHeaderTitle: '',
    appIsActive: false,
    dashboardIsLoading: false,
    showCompanyInput: false,
    appWidth: 767,
  }),
  actions: {
    openIncidentModal() {
      this.incidentModalOpen = true;
    },
    closeIncidentModal() {
      this.incidentModalOpen = false;
    },
    loadingDashboard(isLoading: boolean) {
      this.dashboardIsLoading = isLoading;
    },
    onShowCompanyInput(show: boolean) {
      this.showCompanyInput = show;
    },
    async setBadgeCount() {
      const { apiGet } = useApi();
      const res = await apiGet('/navigation/badgecount', null, false);
      if (res) {
        this.tabBadgeNumbers = res;

        if (this.tabBadgeNumbers.checklists === 0 && this.tabBadgeNumbers.groups === 0 && this.tabBadgeNumbers.warnings === 0 && Capacitor.isNativePlatform()) {
          await PushNotifications.removeAllDeliveredNotifications();
        }
      }
    },
    async setNotificationsList() {
      const list = await PushNotifications.getDeliveredNotifications();
      this.notifications = list.notifications || [];
    },
    async clearNotification(notifications: PushNotificationSchema[]) {
      await PushNotifications.removeDeliveredNotifications({ notifications });
      const list = await PushNotifications.getDeliveredNotifications();
      this.notifications = list.notifications;
    },
    async clearAllNotifications() {
      if (this.notifications.length && Capacitor.isNativePlatform()) {
        await PushNotifications.removeAllDeliveredNotifications();
        this.notifications = [];
      }
    },
    setSegmentOptions(options: SegmentOption[]) {
      this.segmentOptions = options;
    },
    setSegmentValue(value: string) {
      this.segmentValue = value;
    },
    changeSegment(input: { detail: SegmentChangeEventDetail }) {
      this.segmentValue = input.detail.value;
    },
    // Each segmented page sets its own options on enter, so leaving them behind
    // means a later page can render the previous page's segments.
    resetSegments() {
      this.segmentValue = '';
      this.segmentOptions = [];
      this.dynamicHeaderTitle = '';
    },
    setHeaderTitle(title) {
      this.dynamicHeaderTitle = title;
    },
    setAppActive(isActive) {
      this.appIsActive = isActive;
    },
    updateWidth() {
      this.appWidth = window.innerWidth;
    },
  },
  getters: {
    isIncidentModalOpen(): boolean {
      return this.incidentModalOpen;
    },
    checklistNotifications(state): PushNotificationSchema[] {
      return state.notifications.filter(notification => [NotificationType.Checklist].includes(notification.data.type));
    },
    warningsNotifications(state): PushNotificationSchema[] {
      return state.notifications.filter(notification => [NotificationType.Police, NotificationType.Regular, NotificationType.Security].includes(notification.data.type));
    },
    groupNotifications(state): PushNotificationSchema[] {
      return state.notifications.filter(notification => [
        NotificationType.GroupPostPublished,
        NotificationType.GroupPostPending,
        NotificationType.GroupPostComment,
        NotificationType.GroupPostLike,
        NotificationType.GroupPostCommentLike,
        NotificationType.GroupMembershipInvite,
        NotificationType.GroupMembershipRequest,
        NotificationType.GroupMembershipMember,
        NotificationType.GroupChecklistNew,
        NotificationType.EmergencyReplyUpdated,
      ].includes(notification.data.type));
    },
    checklistBadge(): number {
      return this.tabBadgeNumbers.checklists;
      // return this.checklistNotifications.length;
    },
    warningsBadge(): number {
      return this.tabBadgeNumbers.warnings;
      // return this.warningsNotifications.length;
    },
    groupBadge(): number {
      return this.tabBadgeNumbers.groups;
      // return this.groupNotifications.length;
    },
    isDesktopWidth(): boolean {
      return this.appWidth >= 767;
    },
    isDesktop(): boolean {
      if (isPlatform('hybrid')) return false;
      console.log(this.isDesktopWidth);

      return this.isDesktopWidth;
    },
  },
});
